// frame: ewS3j @ ui/dogdisk-agent.pen — 域名防墙·密钥配置(provider 分组卡片)
// DNS 服务商 API Key(GoDaddy / Cloudflare)。真 backend(B2.6.D)。
// 折中对齐(2026-06-25 spec):分组卡片 UI + 仅现有 API;无删除;env 锁 production。
import { useState } from 'react'
import { Button, Spinner, formatDate } from '@dogdisk/shared'
import { AgentLayout } from '../components/agent-layout'
import {
  useDnsCredentials,
  useSetDnsCredential,
  useTestDnsCredential,
  type CredentialStatusRow,
  type SetCredentialInput,
} from '../_lib/jumpdomain-api'

export const PROVIDERS = [
  { key: 'godaddy', label: 'GoDaddy' },
  { key: 'cloudflare', label: 'Cloudflare' },
] as const

export type ProviderKey = (typeof PROVIDERS)[number]['key']

export function groupByProvider(
  rows: CredentialStatusRow[],
): Record<ProviderKey, CredentialStatusRow[]> {
  const out: Record<ProviderKey, CredentialStatusRow[]> = { godaddy: [], cloudflare: [] }
  for (const r of rows) {
    if (r.provider === 'godaddy' || r.provider === 'cloudflare') out[r.provider].push(r)
  }
  return out
}

type DialogState =
  | { mode: 'add'; provider: ProviderKey }
  | { mode: 'edit'; provider: ProviderKey; row: CredentialStatusRow }

export function AgentDomainGuardKeysPage() {
  const { data, isLoading, isError } = useDnsCredentials()
  const groups = groupByProvider(data?.credentials ?? [])
  const setCred = useSetDnsCredential()
  const [dialog, setDialog] = useState<DialogState | null>(null)

  return (
    <AgentLayout topbarTitle="域名防墙 · 密钥配置">
      <div className="space-y-6 p-6" data-testid="ewS3j">
        <header>
          <h1 className="text-2xl font-bold text-gray-900">密钥配置</h1>
          <p className="mt-2 text-sm text-gray-500">
            统一管理 GoDaddy 与 Cloudflare 的 API Key，同一服务商可添加多个密钥用于多账号场景
          </p>
        </header>

        {isLoading ? (
          <Spinner />
        ) : isError ? (
          <p className="py-6 text-sm text-red-600" data-testid="dgk-load-error">加载密钥失败,请刷新重试</p>
        ) : (
          PROVIDERS.map((p) => (
            <ProviderCard
              key={p.key}
              provider={p.key}
              title={p.label}
              rows={groups[p.key]}
              onAdd={() => setDialog({ mode: 'add', provider: p.key })}
              onEdit={(row) => setDialog({ mode: 'edit', provider: p.key, row })}
            />
          ))
        )}
      </div>

      {dialog && (
        <CredentialDialog
          state={dialog}
          onClose={() => setDialog(null)}
          onSubmit={async (input) => {
            await setCred.mutateAsync(input)
            setDialog(null)
          }}
        />
      )}
    </AgentLayout>
  )
}

function ProviderCard({
  provider,
  title,
  rows,
  onAdd,
  onEdit,
}: {
  provider: ProviderKey
  title: string
  rows: CredentialStatusRow[]
  onAdd: () => void
  onEdit: (row: CredentialStatusRow) => void
}) {
  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
      data-testid={`dgk-card-${provider}`}
    >
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900">{title}</h2>
        <Button size="sm" onClick={onAdd} data-testid={`dgk-add-${provider}`}>
          + 添加密钥
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-400" data-testid={`dgk-empty-${provider}`}>
          尚无密钥
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {rows.map((r) => (
            <KeyRow key={r.id} row={r} onEdit={() => onEdit(r)} />
          ))}
        </ul>
      )}
    </section>
  )
}

function KeyRow({ row, onEdit }: { row: CredentialStatusRow; onEdit: () => void }) {
  const testCred = useTestDnsCredential()
  const [testing, setTesting] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  const onTest = async () => {
    setTesting(true)
    setResult(null)
    try {
      const res = await testCred.mutateAsync(row.id)
      setResult(`✓ ${res.provider} OK — ${res.zones_count} zones`)
    } catch (e) {
      setResult(`✗ ${e instanceof Error ? e.message : 'test failed'}`)
    } finally {
      setTesting(false)
    }
  }

  return (
    <li className="py-3" data-testid={`dgk-row-${row.id}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">{row.label || '默认凭据'}</p>
          <p className="font-mono text-xs text-gray-400">{row.masked_token}</p>
          <p className="text-xs text-gray-400">
            {row.last_used_at ? `最后使用: ${formatDate(row.last_used_at)}` : '未使用'}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={onTest}
            disabled={testing}
            data-testid={`dgk-test-${row.id}`}
          >
            {testing ? '测试中…' : '测试'}
          </Button>
          <Button size="sm" variant="secondary" onClick={onEdit} data-testid={`dgk-edit-${row.id}`}>
            编辑
          </Button>
        </div>
      </div>
      {result && (
        <div
          className={`mt-2 text-xs ${result.startsWith('✓') ? 'text-green-600' : 'text-red-600'}`}
          data-testid={`dgk-test-result-${row.id}`}
        >
          {result}
        </div>
      )}
    </li>
  )
}

function CredentialDialog({
  state,
  onClose,
  onSubmit,
}: {
  state: DialogState
  onClose: () => void
  onSubmit: (input: SetCredentialInput) => Promise<void>
}) {
  const isEdit = state.mode === 'edit'
  const provider = state.provider
  const needSecret = provider === 'godaddy'
  const initialLabel = isEdit ? state.row.label : ''
  const initialAccount = isEdit ? state.row.account_id : ''

  const [label, setLabel] = useState(initialLabel)
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')
  const [accountId, setAccountId] = useState(initialAccount)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      // 后端 PUT 为 full-replace 语义(UPSERT 键=provider+label):
      // label/account_id 留空时不发送字段,后端按 NULL/清空处理 —— 与现状一致。
      const input: SetCredentialInput = { provider, api_key: apiKey, env: 'production' }
      if (label) input.label = label
      if (needSecret) input.api_secret = apiSecret
      if (accountId) input.account_id = accountId
      await onSubmit(input)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'submit failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      data-testid="dgk-dialog"
    >
      <form className="w-full max-w-md rounded-lg bg-white p-6" onSubmit={submit}>
        <h3 className="mb-4 text-lg font-semibold">{isEdit ? '编辑 DNS 密钥' : '添加 DNS 密钥'}</h3>

        <label className="mb-3 block">
          <span className="text-sm">Provider</span>
          <input
            className="mt-1 w-full rounded border bg-gray-50 p-2 capitalize text-gray-500"
            value={provider}
            disabled
            readOnly
            data-testid="dgk-provider"
          />
        </label>

        <label className="mb-3 block">
          <span className="text-sm">标签{isEdit ? '' : '(可选)'}</span>
          <input
            className="mt-1 w-full rounded border p-2 disabled:bg-gray-50 disabled:text-gray-500"
            value={label}
            disabled={isEdit}
            onChange={(e) => setLabel(e.target.value)}
            data-testid="dgk-label"
          />
        </label>

        <label className="mb-3 block">
          <span className="text-sm">API Key</span>
          <input
            className="mt-1 w-full rounded border p-2 font-mono"
            type="password"
            required
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={isEdit ? '重新输入以更新密钥' : undefined}
            data-testid="dgk-api-key"
          />
        </label>

        {needSecret && (
          <label className="mb-3 block">
            <span className="text-sm">API Secret(GoDaddy 需要)</span>
            <input
              className="mt-1 w-full rounded border p-2 font-mono"
              type="password"
              required
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              data-testid="dgk-api-secret"
            />
          </label>
        )}

        <label className="mb-3 block">
          <span className="text-sm">Account ID(可选)</span>
          <input
            className="mt-1 w-full rounded border p-2"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            data-testid="dgk-account-id"
          />
        </label>

        {err && <p className="mb-3 text-xs text-red-600">{err}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" disabled={busy} data-testid="dgk-submit">
            {busy ? '保存中…' : '保存'}
          </Button>
        </div>
      </form>
    </div>
  )
}
